import styled from '@emotion/styled';
import {
  palette,
  spacing,
  radius,
  shadow,
  typography,
  transition,
  duration,
  easing,
  layout,
  zIndex,
} from '../../style-utils/theme';
import { up, down } from '../../style-utils/media-queries';
import { scaleIn } from '../../style-utils/motion';
import { m } from '../../motion';

/**
 * The bell and its panel.
 *
 * Two shapes from one component: a popover anchored to the bell on tablet and
 * up, and a full-width bottom sheet on phones — rule 6 of the responsive
 * section of the design system. The rail this usually sits in becomes a bottom
 * tab bar under `md`, so a popover there would open off-screen.
 *
 * BOTH ARE PORTALLED TO <body> NOW (see ../modal/modal-portal.tsx). The panel
 * used to be an inline `position: absolute` child of the bell, which meant the
 * phone sheet — `position: fixed` — was still laid out INSIDE whatever
 * stacking context the rail or header had established, and an entrance
 * transform on either trapped it under the sticky header. Portalled, the
 * popover is `fixed` too and takes its coordinates from the bell's measured
 * rect, the same way the account menu does.
 */

export const BellWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BellButton = styled.button<{ isOpen?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* touchTarget, not the icon's own size: hit area grows with padding. */
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.sm};
  cursor: pointer;
  background: ${({ isOpen }) => (isOpen ? palette.irisTint : 'transparent')};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  &:active {
    transform: scale(0.97);
    transition: transform ${duration.instant} ${easing.spring};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/**
 * The count. Absent entirely at zero rather than rendered as "0" — an empty
 * bell should look calm, not like a counter that happens to read nothing.
 */
export const Badge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 ${spacing.half};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radius.pill};
  background: ${palette.alertStrong};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  /* Against the rail so the pill reads as a separate object on any background. */
  /* The white ring that lifts the badge off the bell. An OUTLINE, not a
     box-shadow: 0 0 0 2px white is a shadow on no token scale (the audit
     scanner flagged it on every screen), and an outline draws the same ring,
     following the pill radius. NO BACKTICKS IN THIS COMMENT. */
  outline: 2px solid ${palette.surface};
  outline-offset: 0;
  animation: ${scaleIn} ${duration.base} ${easing.spring};
`;

export const Scrim = m.div`
  /* Phones only. The popover on larger screens closes on outside-click without
     a scrim, because covering the desk view to read a notification list is a
     heavier gesture than the content deserves. */
  display: none;

  ${down('md')} {
    display: block;
    position: fixed;
    inset: 0;
    background: ${palette.overlay};
    /* The overlay tier, not the modal one. The sheet this backs is an ordinary
       top-level surface, so it belongs on the ordinary scrim tier — the same
       pair every other modal in the app uses: overlay for the scrim, modal for
       the panel. It sat a full tier higher before, which put the notification
       list above every real dialog in the product. */
    z-index: ${zIndex.overlay};
  }
`;

/**
 * Two anchors, because the bell has two homes.
 *
 * 'rail'   — the staff shell's vertical icon rail on the left. The panel opens
 *            to its RIGHT and hangs downward; opening downward alone would run
 *            it off the bottom of a rail whose icons sit near the top.
 * 'header' — the resident dashboard's top bar. The panel drops BELOW the bell
 *            and is right-aligned, so it stays inside the viewport for a bell
 *            that sits near the right edge.
 *
 * Both collapse to the same bottom sheet under `md`.
 *
 * The $top/$left/$right numbers are the bell's measured rect (see the
 * component); they only apply from `md` up, where the popover shape holds.
 */
export type PanelPlacement = 'rail' | 'header';

export const Panel = m.div<{
  placement: PanelPlacement;
  $top?: number;
  $left?: number;
  $right?: number;
}>`
  position: fixed;
  z-index: ${zIndex.dropdown};
  width: 380px;
  /* dvw excludes the desktop scrollbar gutter that vw counts; vw stays as the
     fallback line. */
  max-width: calc(100vw - ${spacing.md});
  max-width: calc(100dvw - ${spacing.md});
  max-height: 70vh;
  max-height: 70dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  transform-origin: ${({ placement }) =>
    placement === 'header' ? 'top right' : 'top left'};

  ${up('md')} {
    ${({ $top }) => ($top !== undefined ? `top: ${$top}px;` : '')}
    ${({ $left }) => ($left !== undefined ? `left: ${$left}px;` : '')}
    ${({ $right }) => ($right !== undefined ? `right: ${$right}px;` : '')}
  }

  /* Bottom sheet on phones, where the rail is a bottom tab bar. */
  ${down('md')} {
    /* One step above the scrim it sits on. The modalTop tier is reserved for a
       dialog raised from INSIDE a modal (theme.ts) and this is not that — a
       bell sheet opened from the rail is a first-class surface, so it takes the
       modal tier like any other. */
    z-index: ${zIndex.modal};
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100%;
    max-width: 100%;
    max-height: 80vh;
    max-height: 80dvh;
    border-radius: ${radius.md} ${radius.md} 0 0;
    border-bottom: none;
    transform-origin: bottom center;
    /* The sheet is flush to bottom: 0, which on a notched iPhone is UNDER the
       home indicator, not above it. The list's last row is the one that loses,
       and it is a row somebody is trying to tap. Padding rather than a bottom
       offset so the sheet still reads as attached to the bottom edge. This
       resolves to 0px on every non-notched device — and it resolved to 0px
       everywhere until viewport-fit=cover was added to public/index.html. */
    padding-bottom: var(--safe-bottom);
  }
`;

export const PanelHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  border-bottom: 1px solid ${palette.line};
  background: ${palette.surface};
  /* Pinned. "Mark all read" has to stay reachable from the bottom of a long
     list, which is exactly where somebody decides they have had enough of it. */
  flex: 0 0 auto;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  letter-spacing: ${typography.tracking.snug};
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

export const TextButton = styled.button`
  border: none;
  background: transparent;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.xs};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.irisDark};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  &:disabled {
    color: ${palette.slateLight};
    cursor: default;
    background: transparent;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const PanelList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  flex: 1 1 auto;
  /* ── THE LINE THAT MAKES THE PANEL SCROLL AT ALL ──
     Without it the list does not scroll and the notifications past the first
     screenful are simply unreachable — no scrollbar, no overflow, just gone.

     A flex item defaults to min-height:auto, which in a column container means
     "never shrink below my content". So the overflow-y:auto above had nothing
     to act on: the list grew to the full height of every row, the Panel's own
     max-height:70vh and overflow:hidden clipped it, and the clipped part had no
     scroll container to reach it with. min-height:0 releases that floor, at
     which point max-height finally constrains the list and the overflow becomes
     a scroll. */
  min-height: 0;
`;

/* The row's opacity is animated by the component (it is how a row arrives, and
   how a muted one stays quiet — see the note there), so it is NOT set here:
   an inline animated value would override a stylesheet one anyway. Likewise
   no `transform` in the transition list — the entrance is driven per frame,
   and a CSS transition on the same property would drag behind it. */
export const Item = m.li<{ unread: boolean }>`
  display: flex;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  border-bottom: 1px solid ${palette.line};
  cursor: pointer;
  background: ${({ unread }) => (unread ? palette.irisTintSoft : palette.surface)};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTint};
  }

  &:last-of-type {
    border-bottom: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/**
 * The severity marker. A dot rather than a coloured row: the row background
 * already carries the read state, and two colour systems on one row is how a
 * list stops being readable at a glance.
 */
export const SeverityDot = styled.span<{ severity: string }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 8px;
  border-radius: ${radius.circle};
  background: ${({ severity }) =>
    severity === 'critical'
      ? palette.alert
      : severity === 'warning'
        ? palette.warning
        : palette.iris};
`;

export const ItemBody = styled.div`
  min-width: 0;
  flex: 1;
`;

export const ItemTitle = styled.p<{ unread: boolean }>`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${({ unread }) =>
    unread ? typography.weight.semibold : typography.weight.regular};
  color: ${palette.ink};
  line-height: ${typography.leading.snug};
`;

export const ItemText = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  line-height: ${typography.leading.normal};
`;

export const ItemTime = styled.time`
  display: block;
  margin-top: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

export const EmptyState = styled.div`
  padding: ${spacing.xl} ${spacing.md};
  text-align: center;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  line-height: ${typography.leading.normal};
`;

export const PanelFooter = styled.footer`
  padding: ${spacing.xs} ${spacing.md} ${spacing.sm};
  border-top: 1px solid ${palette.line};
  display: flex;
  justify-content: center;
  background: ${palette.surface};
  /* Never absorbed into the scroll area: with the list constrained, a footer
     that could shrink would be the first thing to disappear. */
  flex: 0 0 auto;
`;

/**
 * "Live" indicator. Deliberately tiny and unlabelled at rest: it answers "is
 * this list current" for anyone who wonders, and says nothing to anyone who
 * does not. It only turns amber, never red — a dropped socket is a degraded
 * refresh cadence, not an error.
 */
export const LiveDot = styled.span<{ live: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: ${radius.circle};
  flex-shrink: 0;
  background: ${({ live }) => (live ? palette.success : palette.warning)};
  transition: ${transition.colors};
`;
